import React from 'react';
import { useParams } from 'react-router-dom';
import { sessions } from '../data/sessions';
import {
  allExercises as exercises,
  getExercisesBySport,
} from '../data/exercises/index.js';
import { getExerciseTiming } from '../data/timing-source-of-truth.js';
import SportGrid from './SportGrid';
import { useSelectedPlayers } from '../contexts/SelectedPlayersContext';

function WorkoutSelection() {
  const { sport } = useParams();
  const { selectedPlayersData, removePlayer, addPlayer, allPlayersData } =
    useSelectedPlayers();

  // Get exercises for this sport
  const sportExercises = getExercisesBySport(sport);

  // Filter sessions to only show those that have exercises for this sport
  const sportSessions = sessions.filter((session) => {
    // Check if any exercise in the session belongs to this sport
    return session.exercises.some((exerciseId) => {
      const exercise = sportExercises.find((ex) => ex.id === exerciseId);
      return exercise && exercise.sports.includes(sport);
    });
  });

  const sportData = {
    soccer: {
      name: 'Soccer',
      icon: '⚽',
      color: '#4CAF50',
    },
    basketball: {
      name: 'Basketball',
      icon: '🏀',
      color: '#FF9800',
    },
    swimming: {
      name: 'Swimming',
      icon: '🏊',
      color: '#2196F3',
    },
    tennis: {
      name: 'Tennis',
      icon: '🎾',
      color: '#9C27B0',
    },
    volleyball: {
      name: 'Volleyball',
      icon: '🏐',
      color: '#F44336',
    },
    football: {
      name: 'Football',
      icon: '🏈',
      color: '#795548',
    },
    baseball: {
      name: 'Baseball',
      icon: '⚾',
      color: '#FF5722',
    },
    track: {
      name: 'Track & Field',
      icon: '🏃',
      color: '#607D8B',
    },
    gymnastics: {
      name: 'Gymnastics',
      icon: '🤸',
      color: '#E91E63',
    },
    martial_arts: {
      name: 'Martial Arts',
      icon: '🥷',
      color: '#3F51B5',
    },
    dance: {
      name: 'Dance',
      icon: '💃',
      color: '#FF4081',
    },
    lacrosse: {
      name: 'Lacrosse',
      icon: '🥍',
      color: '#8BC34A',
    },
    rugby: {
      name: 'Rugby',
      icon: '🏉',
      color: '#FF9800',
    },
    cricket: {
      name: 'Cricket',
      icon: '🏏',
      color: '#795548',
    },
    golf: {
      name: 'Golf',
      icon: '⛳',
      color: '#4CAF50',
    },
    hockey: {
      name: 'Hockey',
      icon: '🏒',
      color: '#2196F3',
    },
    karate: {
      name: 'Karate',
      icon: '🥋',
      color: '#3F51B5',
    },
    taekwondo: {
      name: 'Taekwondo',
      icon: '☯',
      color: '#3F51B5',
    },
    'martial-arts': {
      name: 'Martial Arts',
      icon: '🥷🏼',
      color: '#3F51B5',
    },
    ballet: {
      name: 'Ballet',
      icon: '🩰',
      color: '#E91E63',
    },
    boxing: {
      name: 'Boxing',
      icon: '🥊',
      color: '#FF5722',
    },
    yoga: {
      name: 'Yoga',
      icon: '🧘🏻‍♀️',
      color: '#4CAF50',
    },
    'movement-coordination': {
      name: 'Gymnastics',
      icon: '🤸',
      color: '#E91E63',
    },
    'running-technique': {
      name: 'Running Technique',
      icon: '🏃',
      color: '#607D8B',
    },
  };

  const currentSport = sportData[sport] || {
    name: sport.charAt(0).toUpperCase() + sport.slice(1).replace('_', ' '),
    icon: '🏆',
    color: '#666',
  };

  const workoutItems = sportSessions.map((session) => {
    // Remove emoji from the title and use it as the icon
    const titleParts = session.title.split(' ');
    const emoji = titleParts[0];
    const cleanTitle = titleParts.slice(1).join(' ');

    // Compute total points as 1 point per minute based on actual audio timing
    const convertExerciseNameToKey = (name) => {
      const nameMap = {
        'Fast Feet': 'fastFeet',
        'High Knees': 'highKnees',
        'Mountain Climbers': 'mountainClimbers',
        'Star Jumps': 'starJumps',
        'Skater Hops': 'skaterHops',
        'One Leg Stand': 'oneLegStand',
        'Side Lunges': 'sideLunges',
        'Big Arm Swings': 'bigArmSwings',
        'Rainbow Reaches': 'rainbowReaches',
        'Frog Jumps': 'frogJumps',
        'Mini Squats': 'miniSquats',
        Burpees: 'burpees',
        'Tiger Pounces': 'tigerPounces',
        'Bear Crawl': 'bearCrawl',
        'Crab Walk': 'crabWalk',
        'Zebra Gallops': 'zebraGallops',
        'Rabbit Hops': 'rabbitHops',
        Plank: 'plank',
        'Push-ups': 'pushUps',
        'Superman Holds': 'supermanHolds',
        'Wall Sits': 'wallSits',
        'Bridge Holds': 'bridgeHolds',
        'Upper Cuts': 'upperCuts',
        'Shoulder Taps': 'shoulderTaps',
        'Tricep Dips': 'tricepDips',
        'Inchworm Walk': 'inchwormWalk',
        'Arm Circles': 'armCircles',
        'Jumping Jacks': 'jumpingJacks',
        // Advanced exercises
        'Mountain Climber Burpees': 'mountainclimberburpees',
        'Jump Squats': 'jumpsquats',
        'Tuck Jumps': 'tuckjumps',
        'Split Jumps': 'splitjumps',
        'Step Jumps': 'stepjumps',
        'Sprint Intervals': 'sprintintervals',
        'Diamond Push-ups': 'diamondpush-ups',
        'Pike Push-ups': 'pikepush-ups',
        'Single Leg Squats': 'single-legsquats',
        'L-Sit Hold': 'l-sithold',
        'Dragon Walk': 'dragonwalk',
        'Single Leg Deadlifts': 'single-legdeadlifts',
        Thrusters: 'thrusters',
      };
      return nameMap[name] || name;
    };

    const totalSeconds = session.exercises.reduce((sum, exerciseId) => {
      const ex = exercises.find((e) => e.id === exerciseId);
      if (!ex) return sum;
      const key = convertExerciseNameToKey(ex.name);
      const timing = getExerciseTiming(key);
      return sum + (timing?.combinedTime || 0);
    }, 0);

    // Calculate actual duration and points from timing data
    const actualMinutes = Math.round(totalSeconds / 60);
    const actualPoints = actualMinutes; // 1 point per minute

    return {
      icon: emoji,
      title: cleanTitle,
      subtitle: `${actualMinutes} Min • ${session.exercises.length} exercises • ${actualPoints} points`,
      color: currentSport.color,
      link: `${sport}/${session.workoutId}`,
    };
  });

  return (
    <SportGrid
      title={`${currentSport.name} Workouts`}
      subtitle='Choose a workout to get started!'
      items={workoutItems}
      showBackButton={true}
      backButtonText='← Back to Sports'
      backButtonPath='/workouts/sports'
      itemLinkPrefix='/workout/'
      selectedPlayers={selectedPlayersData}
      onRemovePlayer={removePlayer}
      onAddPlayer={addPlayer}
      allPlayers={allPlayersData}
    />
  );
}

export default WorkoutSelection;
